import type { ClinicNAP } from "./clinic";

export type NapPreviewField =
  | "name"
  | "alternateName"
  | "street"
  | "locality"
  | "region"
  | "postalCode"
  | "country"
  | "countryCode"
  | "latitude"
  | "longitude"
  | "phoneInternational"
  | "phoneDisplay"
  | "whatsappNumber"
  | "whatsappUrl"
  | "openingHours"
  | "website"
  | "instagram";

export interface NapPreviewItem {
  field: NapPreviewField;
  label: string;
  /** Value the admin entered into the form (canonical source of truth). */
  expected: string | null;
  /** Value found inside the JSON-LD @graph that will be published. */
  schema: string | null;
  ok: boolean;
  /** Human-readable mismatch reason. Empty when ok. */
  reason: string;
  /** Form field name(s) to highlight when this check fails. */
  formFields: string[];
}

export interface NapPreviewResult {
  ok: boolean;
  checkedAt: number;
  items: NapPreviewItem[];
  errorCount: number;
  /** Field names that failed — useful for highlighting form inputs. */
  failingFormFields: Set<string>;
}

/* ----------------------------------------------------------------------- */
/* Helpers                                                                  */
/* ----------------------------------------------------------------------- */

const flattenSchemaNodes = (input: unknown): Record<string, unknown>[] => {
  const nodes: Record<string, unknown>[] = [];
  const walk = (v: unknown) => {
    if (!v || typeof v !== "object") return;
    if (Array.isArray(v)) {
      v.forEach(walk);
      return;
    }
    const obj = v as Record<string, unknown>;
    if ("@type" in obj) nodes.push(obj);
    if (Array.isArray(obj["@graph"])) (obj["@graph"] as unknown[]).forEach(walk);
    Object.values(obj).forEach((c) => {
      if (c && typeof c === "object") walk(c);
    });
  };
  walk(input);
  return nodes;
};

const findClinicNode = (graph: unknown): Record<string, unknown> | null => {
  const nodes = flattenSchemaNodes(graph);
  return (
    nodes.find((n) => {
      const t = n["@type"];
      const types = Array.isArray(t) ? t : typeof t === "string" ? [t] : [];
      return types.some((x) =>
        ["LocalBusiness", "MedicalBusiness", "MedicalOrganization", "MedicalClinic"].includes(x),
      );
    }) || null
  );
};

const canonicalPhoneDigits = (raw: string, defaultCc = "44"): string => {
  if (!raw) return "";
  const trimmed = raw.replace(/[^\d+]/g, "");
  if (trimmed.startsWith("+")) return trimmed.slice(1);
  if (trimmed.startsWith("00")) return trimmed.slice(2);
  if (trimmed.startsWith("0")) return defaultCc + trimmed.slice(1);
  return trimmed;
};

const eq = (a: string | null, b: string | null) =>
  (a ?? "").replace(/\s+/g, " ").trim().toLowerCase() ===
  (b ?? "").replace(/\s+/g, " ").trim().toLowerCase();

const num = (v: unknown): number | null => {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

/* ----------------------------------------------------------------------- */
/* Main check                                                               */
/* ----------------------------------------------------------------------- */

/**
 * Compare the form's `previewClinic` against the JSON-LD `@graph` it produces.
 * This validates that every NAP field the admin entered is faithfully
 * represented in the structured data Google will read.
 */
export const runNapPreviewCheck = (
  clinic: ClinicNAP,
  graph: unknown,
): NapPreviewResult => {
  const node = findClinicNode(graph);
  const addr =
    (node?.address as Record<string, unknown> | undefined) ?? undefined;
  const geo = (node?.geo as Record<string, unknown> | undefined) ?? undefined;
  const sameAs = Array.isArray(node?.sameAs)
    ? (node!.sameAs as unknown[]).map((s) => String(s))
    : [];

  // Find the WhatsApp ContactPoint (if any) inside contactPoint[].
  const contactPoints = Array.isArray(node?.contactPoint)
    ? (node!.contactPoint as Array<Record<string, unknown>>)
    : [];
  const whatsappNode =
    contactPoints.find((c) => {
      const v = String(c.contactType ?? "").toLowerCase();
      return v.includes("whats") || v.includes("messaging");
    }) || null;
  const schemaWhatsapp = whatsappNode
    ? String(whatsappNode.telephone ?? "")
    : "";

  const items: NapPreviewItem[] = [];

  const push = (
    field: NapPreviewField,
    label: string,
    expected: string | null,
    schema: string | null,
    ok: boolean,
    reason: string,
    formFields: string[],
  ) => {
    items.push({ field, label, expected, schema, ok, reason, formFields });
  };

  if (!node) {
    push(
      "name",
      "Clinic JSON-LD node",
      clinic.name,
      null,
      false,
      "No LocalBusiness / MedicalOrganization node found in the @graph.",
      ["name"],
    );
    return finalise(items);
  }

  // Identity
  push(
    "name",
    "Clinic name",
    clinic.name,
    String(node.name ?? "") || null,
    eq(clinic.name, String(node.name ?? "")),
    eq(clinic.name, String(node.name ?? "")) ? "" : "Form name does not match JSON-LD name.",
    ["name"],
  );

  if (clinic.alternateName) {
    const schemaAlt = node.alternateName ? String(node.alternateName) : null;
    push(
      "alternateName",
      "Alternate name",
      clinic.alternateName,
      schemaAlt,
      eq(clinic.alternateName, schemaAlt),
      eq(clinic.alternateName, schemaAlt)
        ? ""
        : "Alternate name is missing or differs in JSON-LD.",
      ["alternate_name"],
    );
  }

  // Address
  const addrChecks: Array<[NapPreviewField, string, string, string, string]> = [
    ["street", "Street address", clinic.address.street, String(addr?.streetAddress ?? ""), "street"],
    ["locality", "Locality", clinic.address.locality, String(addr?.addressLocality ?? ""), "locality"],
    ["region", "Region", clinic.address.region, String(addr?.addressRegion ?? ""), "region"],
    ["postalCode", "Postcode", clinic.address.postalCode, String(addr?.postalCode ?? ""), "postal_code"],
    ["country", "Country", clinic.address.country, String(addr?.addressCountry ?? ""), "country"],
  ];
  addrChecks.forEach(([f, label, exp, sch, formField]) => {
    const ok = eq(exp, sch);
    push(f, label, exp, sch || null, ok, ok ? "" : `Form value "${exp}" ≠ JSON-LD value "${sch || "—"}".`, [formField]);
  });

  // Geo
  const expLat = num(clinic.geo.latitude);
  const expLng = num(clinic.geo.longitude);
  const schLat = num(geo?.latitude);
  const schLng = num(geo?.longitude);
  if (expLat != null || schLat != null) {
    const ok =
      expLat != null && schLat != null && Math.abs(expLat - schLat) < 1e-4;
    push(
      "latitude",
      "Latitude",
      expLat?.toString() ?? null,
      schLat?.toString() ?? null,
      ok,
      ok ? "" : "Latitude in JSON-LD does not match the form value.",
      ["latitude"],
    );
  }
  if (expLng != null || schLng != null) {
    const ok =
      expLng != null && schLng != null && Math.abs(expLng - schLng) < 1e-4;
    push(
      "longitude",
      "Longitude",
      expLng?.toString() ?? null,
      schLng?.toString() ?? null,
      ok,
      ok ? "" : "Longitude in JSON-LD does not match the form value.",
      ["longitude"],
    );
  }

  // Phone — compare in canonical E.164 digits so "020 …" == "+44 20 …".
  const expPhoneDigits = canonicalPhoneDigits(clinic.phone.international);
  const schPhoneDigits = canonicalPhoneDigits(String(node.telephone ?? ""));
  const phoneOk = !!expPhoneDigits && expPhoneDigits === schPhoneDigits;
  push(
    "phoneInternational",
    "Phone (international)",
    clinic.phone.international,
    String(node.telephone ?? "") || null,
    phoneOk,
    phoneOk
      ? ""
      : `Phone digits differ: form "${expPhoneDigits || "—"}" vs schema "${schPhoneDigits || "—"}".`,
    ["phone_international", "phone_display"],
  );

  // Display phone parity: digits must match the international form.
  const dispDigits = canonicalPhoneDigits(clinic.phone.display);
  const dispOk = !!dispDigits && dispDigits === expPhoneDigits;
  push(
    "phoneDisplay",
    "Phone (display)",
    clinic.phone.display,
    clinic.phone.international,
    dispOk,
    dispOk
      ? ""
      : "Display phone digits don't match the international number.",
    ["phone_display", "phone_international"],
  );

  // WhatsApp (only if the admin filled one in)
  if (clinic.whatsapp.number || schemaWhatsapp) {
    const expWa = canonicalPhoneDigits(clinic.whatsapp.number);
    const schWa = canonicalPhoneDigits(schemaWhatsapp);
    const ok = !!expWa && expWa === schWa;
    push(
      "whatsappNumber",
      "WhatsApp number",
      clinic.whatsapp.number || null,
      schemaWhatsapp || null,
      ok,
      ok
        ? ""
        : "WhatsApp number is missing from JSON-LD or doesn't match the form.",
      ["whatsapp_number"],
    );

    // URL must contain the WhatsApp digits (wa.me/447…).
    if (clinic.whatsapp.url) {
      const urlDigits = clinic.whatsapp.url.replace(/[^\d]/g, "");
      const urlOk = !!expWa && urlDigits.endsWith(expWa);
      push(
        "whatsappUrl",
        "WhatsApp link",
        clinic.whatsapp.url,
        sameAs.find((s) => /wa\.me/i.test(s)) ?? null,
        urlOk,
        urlOk
          ? ""
          : "WhatsApp URL doesn't include the same number as the WhatsApp field.",
        ["whatsapp_url", "whatsapp_number"],
      );
    }
  }

  // Opening hours — compare the schema-formatted strings.
  const expHours = clinic.hours
    .filter((r) => r.opens && r.closes)
    .map((r) => `${r.schema.join(",")} ${r.opens}-${r.closes}`)
    .sort();
  const rawSchemaHours = node.openingHours;
  const schHours = (Array.isArray(rawSchemaHours)
    ? (rawSchemaHours as unknown[]).map((h) => String(h))
    : rawSchemaHours
    ? [String(rawSchemaHours)]
    : []
  ).sort();
  const hoursOk =
    expHours.length === schHours.length &&
    expHours.every((v, i) => v === schHours[i]);
  push(
    "openingHours",
    "Opening hours",
    expHours.join(" · ") || null,
    schHours.join(" · ") || null,
    hoursOk,
    hoursOk ? "" : "Opening hours in JSON-LD don't match the form rows.",
    [],
  );

  // Social links (must appear in sameAs)
  if (clinic.social.website) {
    const ok = sameAs.includes(clinic.social.website);
    push(
      "website",
      "Website",
      clinic.social.website,
      sameAs.find((s) => s === clinic.social.website) ?? null,
      ok,
      ok ? "" : "Website URL is missing from JSON-LD sameAs.",
      ["website_url"],
    );
  }
  if (clinic.social.instagram) {
    const ok = sameAs.includes(clinic.social.instagram);
    push(
      "instagram",
      "Instagram",
      clinic.social.instagram,
      sameAs.find((s) => s === clinic.social.instagram) ?? null,
      ok,
      ok ? "" : "Instagram URL is missing from JSON-LD sameAs.",
      ["instagram_url"],
    );
  }

  return finalise(items);
};

const finalise = (items: NapPreviewItem[]): NapPreviewResult => {
  const failing = items.filter((i) => !i.ok);
  const failingFormFields = new Set<string>();
  failing.forEach((i) => i.formFields.forEach((f) => failingFormFields.add(f)));
  return {
    ok: failing.length === 0,
    checkedAt: Date.now(),
    items,
    errorCount: failing.length,
    failingFormFields,
  };
};
